/**
 * Generic infinite-scroll hook backed by react-query's `useInfiniteQuery`.
 *
 * Wraps any service that exposes a `listPage({cursor, limit})` returning
 * `{items, next_cursor, has_more, limit}` (the standard shape emitted by
 * our cursor-paginated endpoints).
 *
 * Returns:
 *   - `items`         flattened list of all loaded pages
 *   - `hasMore`       true while the backend reports more pages
 *   - `loadMore`      callable to fetch the next page
 *   - `sentinelRef`   ref to attach to a sentinel <div> at the bottom —
 *                     IntersectionObserver triggers `loadMore` when visible.
 *   - `isLoading`     true while the FIRST page is loading.
 *   - `isFetchingNextPage`
 *
 * @module hooks/useInfiniteList
 */
import { useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

export function useInfiniteList({ queryKey, fetchPage, limit = 30, enabled = true }) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage({ cursor: pageParam ?? "", limit }),
    getNextPageParam: (last) => (last?.has_more ? last.next_cursor : undefined),
    initialPageParam: "",
    enabled,
    staleTime: 10_000,
  });

  const items = useMemo(() => {
    const pages = query.data?.pages || [];
    return pages.flatMap((p) => p?.items || []);
  }, [query.data]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !query.hasNextPage) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: "240px" },  // pre-fetch slightly before the user scrolls in
    );
    io.observe(el);
    return () => io.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  return {
    items,
    hasMore: !!query.hasNextPage,
    loadMore: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
    sentinelRef,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error,
  };
}
